import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiApi } from '../api';

const card = { background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 16 };

const INTERVALS = ['1m', '5m', '15m', '1h', '4h', '1d'];
const SYMBOLS   = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT'];
const TF_COLOR  = { '1m': '#e879f9', '5m': '#34d399', '15m': '#38bdf8', '1h': '#a78bfa', '4h': '#fbbf24', '1d': '#f97316' };
const SYM_COLOR = { BTCUSDT: '#f59e0b', ETHUSDT: '#38bdf8', BNBUSDT: '#fbbf24', SOLUSDT: '#a78bfa' };

const GREEN  = '#34d399';
const RED    = '#f87171';
const YELLOW = '#fbbf24';
const ACCENT = '#38bdf8';
const MUTED  = '#64748b';
const TEXT   = '#94a3b8';
const FAST_TF = ['1m', '5m', '15m']; // ficam velhos rápido: limite 24h

const sectionTitle = { fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 12 };
const btnTiny = {
  border: '1px solid #334155', borderRadius: 6, padding: '3px 8px',
  cursor: 'pointer', fontSize: 10, background: '#0f172a', color: TEXT,
};

const f1Color = (v, good, warn) => (v >= good ? GREEN : v >= warn ? YELLOW : RED);

function relAge(hours) {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}min`;
  if (hours < 48) return `${Math.round(hours)}h`;
  const d = hours / 24;
  return d < 10 ? `${d.toFixed(1)}d` : `${Math.round(d)}d`;
}

function fmtDateTime(iso) {
  const t = new Date(iso);
  if (!iso || isNaN(t.getTime())) return '—';
  return t.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// Frescor do dado (campo `to` do cache):
// 1m/5m/15m → verde < 24h, vermelho acima (sem faixa amarela).
// 1h+       → verde < 24h, amarelo < 7d, vermelho >= 7d.
// Dado velho = decisão em dado velho.
function ageInfo(interval, to, now) {
  const t = new Date(to);
  if (!to || isNaN(t.getTime())) return null;
  const hours = Math.max(0, (now - t.getTime()) / 3.6e6);
  const fast = FAST_TF.includes(interval);
  const stale = fast ? hours >= 24 : hours >= 24 * 7;
  const mid = !stale && !fast && hours >= 24;
  return { hours, stale, color: stale ? RED : mid ? YELLOW : GREEN, label: relAge(hours) };
}

function AgeBadge({ interval, to, now }) {
  const info = ageInfo(interval, to, now);
  if (!info) return <span style={{ fontSize: 10, color: MUTED }}>sem data</span>;
  return (
    <span style={{
      background: `${info.color}1c`, color: info.color, border: `1px solid ${info.color}55`,
      borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      {info.label}
    </span>
  );
}

/* ───────────────────────── Seção 1 · Saúde dos modelos ───────────────────────── */

function HealthRow({ m, interval, onTrain, isPending }) {
  const trained = m?.status === 'trained';
  const metrics = m?.metrics || {};
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '42px 110px 1fr 1fr 56px 96px',
      gap: 8, alignItems: 'center', padding: '7px 10px', borderRadius: 8, fontSize: 12,
      background: trained ? 'transparent' : '#2d0f0f66',
      borderLeft: `3px solid ${trained ? 'transparent' : RED}`,
    }}>
      <span style={{ color: TF_COLOR[interval] || TEXT, fontWeight: 700 }}>{interval}</span>
      {trained ? (
        <>
          <span style={{ color: GREEN, fontWeight: 600, fontSize: 11 }}>● treinado</span>
          <span style={{ fontWeight: 700, color: f1Color(metrics.f1_macro || 0, 0.45, 0.35) }}>
            {metrics.f1_macro ? `${(metrics.f1_macro * 100).toFixed(0)}%` : '—'}
          </span>
          <span style={{ fontWeight: 700, color: metrics.buy_f1 ? f1Color(metrics.buy_f1, 0.35, 0.20) : MUTED }}>
            {metrics.buy_f1 ? `${(metrics.buy_f1 * 100).toFixed(0)}%` : '—'}
          </span>
          <span style={{ color: MUTED, fontSize: 11 }}>{metrics.n_val?.toLocaleString() || '—'}</span>
          <button onClick={onTrain} disabled={isPending} style={btnTiny}>
            {isPending ? '⏳' : '↻ retreinar'}
          </button>
        </>
      ) : (
        <>
          <span style={{ color: RED, fontWeight: 700, fontSize: 11 }}>● não treinado</span>
          <span style={{ color: '#475569' }}>—</span>
          <span style={{ color: '#475569' }}>—</span>
          <span style={{ color: '#475569' }}>—</span>
          <button onClick={onTrain} disabled={isPending} style={{ ...btnTiny, color: ACCENT, borderColor: `${ACCENT}66` }}>
            {isPending ? '⏳' : '+ treinar'}
          </button>
        </>
      )}
    </div>
  );
}

function HealthSymbolGroup({ sym, models, trainStatus, onTrainAll, onTrainOne, trainMutation, trainAllMutation }) {
  const symColor = SYM_COLOR[sym] || TEXT;
  const trained = models.filter(m => m.status === 'trained');
  const avgF1 = trained.length
    ? trained.reduce((s, m) => s + (m.metrics?.f1_macro || 0), 0) / trained.length
    : 0;

  const details = Object.entries(trainStatus?.details || {})
    .filter(([k]) => k.startsWith(`train_${sym}_`))
    .map(([k, v]) => ({ key: k, ...v }));
  const running = details.filter(d => d.status === 'running' || d.status === 'queued');

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: symColor }}>
          {sym.replace('USDT', '')}
          <span style={{ fontSize: 10, color: MUTED, fontWeight: 400, marginLeft: 3 }}>USDT</span>
        </span>
        <span style={{ fontSize: 11, color: MUTED }}>
          {trained.length}/{INTERVALS.length} treinados
        </span>
        {avgF1 > 0 && (
          <span style={{ fontSize: 11, fontWeight: 600, color: f1Color(avgF1, 0.4, 0.3) }}>
            F1 médio {(avgF1 * 100).toFixed(0)}%
          </span>
        )}
        {running.length > 0 && details.map(d => {
          const c = d.status === 'done' ? GREEN : d.status === 'error' ? RED : YELLOW;
          return (
            <span key={d.key} style={{ fontSize: 10, background: `${c}22`, color: c, borderRadius: 4, padding: '2px 6px' }}>
              {d.interval} {d.status === 'done' ? `${((d.f1_macro || 0) * 100).toFixed(0)}%` : d.status === 'running' ? '⏳' : d.status === 'queued' ? '⏸' : '✗'}
            </span>
          );
        })}
        <button
          onClick={() => onTrainAll(sym)}
          disabled={running.length > 0}
          style={{
            marginLeft: 'auto',
            background: running.length > 0 ? '#1a2a1a' : '#052e16',
            color: GREEN, border: '1px solid #34d399', borderRadius: 6,
            padding: '4px 12px', cursor: running.length > 0 ? 'default' : 'pointer',
            fontSize: 11, opacity: running.length > 0 ? 0.7 : 1,
          }}
        >
          {running.length > 0 ? `⏳ ${running.length} treinando...` : '🚀 Treinar Tudo'}
        </button>
      </div>
      {INTERVALS.map(iv => (
        <HealthRow
          key={iv}
          interval={iv}
          m={models.find(x => x.interval === iv)}
          isPending={trainMutation.isPending &&
            trainMutation.variables?.symbol === sym &&
            trainMutation.variables?.interval === iv}
          onTrain={() => onTrainOne(sym, iv)}
        />
      ))}
    </div>
  );
}

function HealthSection({ health, trainStatus, trainMutation, trainAllMutation, onTrainAll, onTrainOne }) {
  const models = health?.models || [];
  return (
    <div style={card}>
      <div style={{ ...sectionTitle, display: 'flex', justifyContent: 'space-between' }}>
        <span>🧠 Saúde dos modelos</span>
        <span style={{ fontSize: 10, fontWeight: 400, color: MUTED }}>F1 out-of-sample</span>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: '42px 110px 1fr 1fr 56px 96px',
        gap: 8, padding: '0 10px 6px', fontSize: 10, color: MUTED, textTransform: 'uppercase',
      }}>
        <span>TF</span><span>Status</span><span>F1 macro</span><span>Buy F1</span><span>Val</span><span>Ação</span>
      </div>
      {SYMBOLS.map(sym => (
        <HealthSymbolGroup
          key={sym}
          sym={sym}
          models={models.filter(m => m.symbol === sym)}
          trainStatus={trainStatus}
          onTrainAll={onTrainAll}
          onTrainOne={onTrainOne}
          trainMutation={trainMutation}
          trainAllMutation={trainAllMutation}
        />
      ))}
    </div>
  );
}

/* ───────────────────────── Seção 2 · Frescor dos dados ───────────────────────── */

function FreshnessSection({ cacheList, dlStatus, downloadAll, download1mMax, now }) {
  const rows = (Array.isArray(cacheList) ? cacheList : [])
    .map(c => ({ ...c, age: ageInfo(c.interval, c.to, now) }))
    .sort((a, b) => (b.age?.hours ?? -1) - (a.age?.hours ?? -1)); // mais velhos primeiro

  const details = Object.entries(dlStatus?.details || {});
  const anyRunning = details.some(([, d]) => d.status === 'running' || d.status === 'queued');
  const oneMRunning = details.some(([k, d]) => k.endsWith('_1m') && d.status === 'running');

  return (
    <div style={card}>
      <div style={{ ...sectionTitle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span>💾 Frescor dos dados</span>
        <span style={{ display: 'flex', gap: 8, fontWeight: 400 }}>
          <button
            onClick={() => downloadAll.mutate()}
            disabled={downloadAll.isPending}
            style={{
              background: '#1e3a5f', color: ACCENT, border: '1px solid #38bdf8',
              borderRadius: 6, padding: '4px 12px', cursor: downloadAll.isPending ? 'default' : 'pointer',
              fontSize: 11, fontWeight: 600, opacity: downloadAll.isPending ? 0.7 : 1,
            }}
          >
            {downloadAll.isPending ? '⏳ Iniciando...' : '⬇ Baixar tudo'}
          </button>
          <button
            onClick={() => download1mMax.mutate()}
            disabled={download1mMax.isPending || oneMRunning}
            title="Baixa 3 anos de 1m (~4 min) — ideal para backtest de longo prazo"
            style={{
              background: '#2d1a5e', color: '#a78bfa', border: '1px solid #a78bfa',
              borderRadius: 6, padding: '4px 12px', cursor: oneMRunning || download1mMax.isPending ? 'default' : 'pointer',
              fontSize: 11, fontWeight: 600, opacity: oneMRunning || download1mMax.isPending ? 0.7 : 1,
            }}
          >
            {oneMRunning ? '⏳ 1m baixando...' : download1mMax.isPending ? '⏳ Iniciando...' : '🕐 Máximo 1m (3 anos)'}
          </button>
        </span>
      </div>

      {/* Status do download em andamento */}
      {dlStatus && (dlStatus.running > 0 || anyRunning) && (
        <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 11 }}>
          <div style={{ color: TEXT, marginBottom: 6 }}>
            Download: {dlStatus.done}/{dlStatus.total} concluídos · <span style={{ color: YELLOW }}>{dlStatus.running} rodando</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
            {details.map(([key, info]) => {
              const c = info.status === 'done' ? GREEN : info.status === 'running' ? YELLOW : info.status === 'error' ? RED : MUTED;
              if (info.status === 'done' && !anyRunning) return null;
              return (
                <div key={key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: TEXT, marginBottom: 2 }}>
                    <span style={{ fontFamily: 'monospace' }}>{key}</span>
                    <span style={{ color: c, fontWeight: 600 }}>
                      {info.status === 'done'
                        ? `✓ ${info.rows?.toLocaleString() || 0}`
                        : info.status === 'running'
                        ? `${info.rows?.toLocaleString() || 0} / ~${info.rows_target?.toLocaleString() || '?'} (${info.pct || 0}%)`
                        : info.status}
                    </span>
                  </div>
                  {info.status === 'running' && info.pct > 0 && (
                    <div style={{ background: '#1e293b', borderRadius: 3, height: 4 }}>
                      <div style={{ background: YELLOW, width: `${info.pct}%`, height: '100%', borderRadius: 3, transition: 'width .5s' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{
        display: 'grid', gridTemplateColumns: '120px 1fr 90px 100px 80px',
        gap: 8, padding: '0 10px 6px', fontSize: 10, color: MUTED, textTransform: 'uppercase',
      }}>
        <span>Par</span><span>Último dado</span><span>Idade</span><span>Candles</span><span>Tamanho</span>
      </div>
      {rows.map((c, i) => {
        const symColor = SYM_COLOR[c.symbol] || TEXT;
        return (
          <div key={`${c.symbol}_${c.interval}_${i}`} style={{
            display: 'grid', gridTemplateColumns: '120px 1fr 90px 100px 80px',
            gap: 8, alignItems: 'center', padding: '6px 10px', borderRadius: 8, fontSize: 12,
            background: c.cached === false ? '#2d0f0f66' : c.age?.stale ? '#2d0f0f44' : 'transparent',
            borderLeft: `3px solid ${c.cached === false ? RED : c.age?.stale ? RED : 'transparent'}`,
          }}>
            <span>
              <span style={{ color: symColor, fontWeight: 700, fontSize: 11 }}>{c.symbol.replace('USDT', '')}</span>
              <span style={{ color: TF_COLOR[c.interval] || TEXT, fontWeight: 700, fontSize: 11, marginLeft: 8 }}>{c.interval}</span>
            </span>
            {c.cached === false ? (
              <span style={{ color: RED, fontWeight: 600, fontSize: 11 }}>sem dados em cache</span>
            ) : (
              <span style={{ color: TEXT, fontFamily: 'monospace', fontSize: 11 }}>{fmtDateTime(c.to)}</span>
            )}
            {c.cached === false
              ? <span style={{ fontSize: 10, color: MUTED }}>—</span>
              : <AgeBadge interval={c.interval} to={c.to} now={now} />}
            <span style={{ color: MUTED, fontSize: 11 }}>{c.cached === false ? '—' : c.rows?.toLocaleString() || '—'}</span>
            <span style={{ color: MUTED, fontSize: 11 }}>{c.cached === false ? '—' : c.size_kb != null ? `${Math.round(c.size_kb).toLocaleString()} KB` : '—'}</span>
          </div>
        );
      })}
      {rows.length === 0 && (
        <div style={{ color: MUTED, fontSize: 12, padding: '8px 10px' }}>
          Cache vazio — clique em ⬇ Baixar tudo para começar.
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Seção 3 · Autotreinador ───────────────────────── */

function TrainerSection({ trainer }) {
  const running = !!trainer?.running;
  const ks = trainer?.knowledge_summary || [];
  const intervalTxt = trainer?.check_interval_seconds != null
    ? (trainer.check_interval_seconds >= 60
        ? `a cada ${Math.round(trainer.check_interval_seconds / 60)} min`
        : `a cada ${trainer.check_interval_seconds}s`)
    : null;

  return (
    <div style={card}>
      <div style={sectionTitle}>🤖 Autotreinador</div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{
          background: running ? '#052e1688' : '#2d0f0f88',
          color: running ? GREEN : RED,
          border: `1px solid ${running ? GREEN : RED}`,
          borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 700,
        }}>
          {running ? '🟢 Rodando' : '🔴 Parado'}
        </span>
        {intervalTxt && <span style={{ fontSize: 12, color: MUTED }}>verifica {intervalTxt}</span>}
        {trainer?.models_tracked != null && (
          <span style={{ fontSize: 12, color: MUTED }}>{trainer.models_tracked} modelos acompanhados</span>
        )}
      </div>

      {ks.length > 0 ? (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: '1.2fr 60px 150px 90px 1.4fr',
            gap: 8, padding: '0 10px 6px', fontSize: 10, color: MUTED, textTransform: 'uppercase',
          }}>
            <span>Modelo</span><span>Versão</span><span>Último treino</span><span>Rows</span><span>Feature principal</span>
          </div>
          {ks.map((k, i) => {
            const t = new Date(k.last_trained);
            const rel = k.last_trained && !isNaN(t.getTime()) ? relAge(Math.max(0, (Date.now() - t.getTime()) / 3.6e6)) : null;
            const [sym, iv] = (k.model || '').split('_');
            return (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '1.2fr 60px 150px 90px 1.4fr',
                gap: 8, alignItems: 'center', padding: '6px 10px', borderRadius: 8, fontSize: 12,
                background: i % 2 ? '#0f172a66' : 'transparent',
              }}>
                <span style={{ fontWeight: 700 }}>
                  <span style={{ color: SYM_COLOR[sym] || TEXT, fontSize: 11 }}>{sym || k.model}</span>
                  <span style={{ color: TF_COLOR[iv] || TEXT, fontSize: 11, marginLeft: 8 }}>{iv || ''}</span>
                </span>
                <span style={{ color: MUTED, fontSize: 11 }}>{k.version != null ? `v${k.version}` : '—'}</span>
                <span style={{ color: TEXT, fontSize: 11, fontFamily: 'monospace' }}>
                  {k.last_trained && !isNaN(t.getTime()) ? `${fmtDateTime(k.last_trained)} · ${rel}` : '—'}
                </span>
                <span style={{ color: MUTED, fontSize: 11 }}>{k.rows?.toLocaleString() || '—'}</span>
                <span style={{ color: ACCENT, fontFamily: 'monospace', fontSize: 11 }}>{k.top_feature || '—'}</span>
              </div>
            );
          })}
        </>
      ) : (
        <div style={{ color: MUTED, fontSize: 12 }}>
          Sem histórico de treino ainda — o autotreinador registra cada rodada aqui.
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Página ───────────────────────── */

export default function Models() {
  const queryClient = useQueryClient();
  const now = Date.now();

  const { data: health, isLoading, refetch } = useQuery({
    queryKey: ['models-health'],
    queryFn: () => aiApi.get('/models/health').then(r => r.data),
    refetchInterval: 30_000,
  });

  const { data: cacheList } = useQuery({
    queryKey: ['cache'],
    queryFn: () => aiApi.get('/cache').then(r => r.data),
    refetchInterval: 30_000,
  });

  const { data: dlStatus } = useQuery({
    queryKey: ['download-status'],
    queryFn: () => aiApi.get('/cache/download-status').then(r => r.data),
    refetchInterval: 3_000,
  });

  const { data: trainer } = useQuery({
    queryKey: ['auto-trainer-status'],
    queryFn: () => aiApi.get('/auto-trainer/status').then(r => r.data),
    refetchInterval: 30_000,
  });

  const invalidateData = () => {
    queryClient.invalidateQueries({ queryKey: ['download-status'] });
    queryClient.invalidateQueries({ queryKey: ['cache'] });
  };

  const downloadAll = useMutation({
    mutationFn: () => aiApi.post(`/cache/download-all?symbols=${SYMBOLS.join(',')}`).then(r => r.data),
    onSuccess: invalidateData,
  });

  const download1mMax = useMutation({
    mutationFn: () => aiApi.post(`/cache/download-1m-max?symbols=${SYMBOLS.join(',')}&days=1095`).then(r => r.data),
    onSuccess: invalidateData,
  });

  const trainAllMutation = useMutation({
    mutationFn: (symbol) => aiApi.post(`/train/all/${symbol}?intervals=1m,5m,15m,1h,4h,1d`).then(r => r.data),
    onSuccess: () => refetch(),
  });

  const trainMutation = useMutation({
    mutationFn: ({ symbol, interval }) => aiApi.post(`/train/${symbol}?interval=${interval}`).then(r => r.data),
    onSuccess: () => refetch(),
  });

  // Resumo para resposta em 5 segundos: algo velho ou não treinado?
  const models = health?.models || [];
  const totalTrained = SYMBOLS.reduce((s, sym) =>
    s + INTERVALS.filter(iv => models.find(m => m.symbol === sym && m.interval === iv && m.status === 'trained')).length, 0);
  const totalModels = SYMBOLS.length * INTERVALS.length;
  const untrainedCount = totalModels - totalTrained;

  const cacheEntries = Array.isArray(cacheList) ? cacheList : [];
  const staleCount = cacheEntries.filter(c =>
    c.cached === false || ageInfo(c.interval, c.to, now)?.stale).length;

  const trainerRunning = !!trainer?.running;
  const problems = untrainedCount > 0 || staleCount > 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Modelos &amp; Dados</h1>
          <p style={{ color: MUTED, fontSize: 13 }}>
            Saúde dos modelos · frescor dos dados · autotreinador
          </p>
        </div>
        <button onClick={() => { refetch(); invalidateData(); queryClient.invalidateQueries({ queryKey: ['auto-trainer-status'] }); }} style={{
          marginLeft: 'auto',
          background: '#1e3a5f', color: ACCENT, border: '1px solid #38bdf8',
          borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12,
        }}>↻ Atualizar</button>
      </div>

      {/* Faixa de resposta rápida: algum modelo velho ou não treinado? */}
      <div style={{
        ...card,
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        background: problems ? '#2d0f0f88' : '#052e1633',
        border: `1px solid ${problems ? RED : GREEN}`,
        padding: 16,
      }}>
        <span style={{ fontSize: 16, fontWeight: 800, color: problems ? RED : GREEN }}>
          {problems ? '⚠ Atenção' : '✓ Tudo saudável'}
        </span>
        {untrainedCount > 0 && (
          <span style={{ fontSize: 12, color: TEXT }}>
            <span style={{ color: RED, fontWeight: 700 }}>{untrainedCount}</span> modelo(s) sem treino
          </span>
        )}
        {staleCount > 0 && (
          <span style={{ fontSize: 12, color: TEXT }}>
            <span style={{ color: RED, fontWeight: 700 }}>{staleCount}</span> fonte(s) de dados velha(s) ou vazia(s)
          </span>
        )}
        {!problems && <span style={{ fontSize: 12, color: TEXT }}>todos os {totalModels} modelos treinados e dados frescos</span>}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: MUTED }}>
          autotreinador: <span style={{ color: trainerRunning ? GREEN : RED, fontWeight: 600 }}>{trainerRunning ? 'rodando' : 'parado'}</span>
        </span>
      </div>

      {/* Resumo */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 18px', flex: 1, minWidth: 120 }}>
          <div style={{ fontSize: 11, color: MUTED, marginBottom: 3 }}>Treinados</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: totalTrained === totalModels ? GREEN : TEXT }}>
            {totalTrained}<span style={{ fontSize: 13, color: '#475569' }}>/{totalModels}</span>
          </div>
        </div>
        <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 18px', flex: 1, minWidth: 120 }}>
          <div style={{ fontSize: 11, color: MUTED, marginBottom: 3 }}>Não treinados</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: untrainedCount > 0 ? RED : GREEN }}>
            {untrainedCount > 0 ? untrainedCount : '✓'}
          </div>
        </div>
        <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 18px', flex: 1, minWidth: 120 }}>
          <div style={{ fontSize: 11, color: MUTED, marginBottom: 3 }}>Dados velhos</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: staleCount > 0 ? RED : GREEN }}>
            {staleCount > 0 ? staleCount : '✓'}
          </div>
        </div>
        <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 18px', flex: 1, minWidth: 120 }}>
          <div style={{ fontSize: 11, color: MUTED, marginBottom: 3 }}>Autotreinador</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: trainerRunning ? GREEN : RED }}>
            {trainerRunning ? '🟢' : '🔴'}
          </div>
        </div>
      </div>

      {isLoading && (
        <div style={{ textAlign: 'center', color: MUTED, padding: 40 }}>Carregando modelos...</div>
      )}

      {!isLoading && (
        <>
          <HealthSection
            health={health}
            trainStatus={dlStatus}
            trainMutation={trainMutation}
            trainAllMutation={trainAllMutation}
            onTrainAll={(s) => trainAllMutation.mutate(s)}
            onTrainOne={(s, iv) => trainMutation.mutate({ symbol: s, interval: iv })}
          />
          <FreshnessSection
            cacheList={cacheEntries}
            dlStatus={dlStatus}
            downloadAll={downloadAll}
            download1mMax={download1mMax}
            now={now}
          />
          <TrainerSection trainer={trainer} />
        </>
      )}
    </div>
  );
}
